import { fromEvent, merge } from 'rxjs';
import { filter, map, scan, startWith, tap } from 'rxjs/operators';

/** Constants */
const NO_ACTIVE_INDEX = -1;
const DEFAULT_ACTIVE_INDEX = 0;

/**
 * Get next index (non-circular)
 */
const getNextIndex = (current, delta, length) => {
  if (length <= 0) { return NO_ACTIVE_INDEX; }
  const next = current + delta;
  if (next < 0) { return 0; } // stay at first
  if (next >= length) { return length - 1; } // stay at last
  return next;
};

/**
 * Check if index is valid
 */
const isValidIndex = (index, length) =>
  index !== NO_ACTIVE_INDEX && index >= 0 && index < length;

/**
 * Scroll item into view only if needed
 */
const scrollIntoViewIfNeeded = (item, shouldScroll) => {
  if (shouldScroll && item) {
    item.scrollIntoView({ block: 'nearest' });
  }
};

/**
 * Update active class on items
 */
export const updateActiveClass = (items, activeClass, index) => {
  items.forEach((item, i) =>
    item.classList.toggle(activeClass, i === index)
  );
};

/**
 * Focus the input container
 */
const focusInputContainer = (inputElement, isMobile) => {
  if (isMobile) {
    inputElement.focus(); // focus on mobile only
  }
  // On desktop, keep input focused, do not focus container
}

/**
 * Main functional navigator
 */
export const createListNavigator = (
  inputElement, container,
  { getItems, onEnter, isMobile = false, activeClass = 'active' }
) => {
  const list = () => getItems() || [];

  const key$ = fromEvent(inputElement, 'keydown');

  const navigation$ = key$.pipe(
    filter((e) => e.key === 'ArrowDown' || e.key === 'ArrowUp'),
    tap((e) => e.preventDefault()),
    map((e) => e.key === 'ArrowDown' ? 1 : -1),
    startWith(DEFAULT_ACTIVE_INDEX),
    scan((index, delta) => getNextIndex(index, delta, list().length)),
    tap((index, sourceIndex) => {
      const items = list();
      const prevIndex = sourceIndex?.value ?? DEFAULT_ACTIVE_INDEX;
      const reachedExtreme =
        (index === 0 && prevIndex === 0) ||
        (index === items.length - 1 && prevIndex === items.length - 1);

      updateActiveClass(items, activeClass, index);
      focusInputContainer(inputElement, isMobile);
      if (isValidIndex(index, items.length)) {
        // only scroll when not stuck at top/bottom
        scrollIntoViewIfNeeded(items[index], !reachedExtreme);
      }
    })
  );

  /** Handle Enter key */
  const enter$ = key$.pipe(
    filter((e) => e.key === 'Enter'),
    tap((e) => e.preventDefault()),
    tap(() => {
      const items = list();
      const active = items.findIndex((item) =>
        item.classList.contains(activeClass)
      );
      if (onEnter && isValidIndex(active, items.length)) {
        onEnter(items[active], active);
      }
    })
  );

  /** Handle Click */
  const click$ = fromEvent(container, 'click').pipe(
    map((e) => {
      const item = e.target.closest('[data-search-bar-result-item]');
      return item || null;
    }),
    filter(Boolean),
    tap((item) => {
      const items = list();
      const index = items.indexOf(item);
      updateActiveClass(items, activeClass, index);
      if (onEnter && isValidIndex(index, items.length)) {
        onEnter(item, index);
      }
    })
  );

  /** Merge all event subscriptions */
  const subscription = merge(navigation$, enter$, click$).subscribe();

  // Select first item immediately
  const initialItems = list();
  if (initialItems.length > 0) {
    updateActiveClass(initialItems, activeClass, DEFAULT_ACTIVE_INDEX);
  }

  return {
    destroy() {
      subscription.unsubscribe();
    },
  };
};
